/* eslint-disable camelcase */
import {
    ARCHITECTURE_1oo1,
    ARCHITECTURE_1oo2,
    ARCHITECTURE_1oo2D,
    ARCHITECTURE_1oo3,
    ARCHITECTURE_2oo2,
    ARCHITECTURE_2oo3,
    ARCHITECTURE_2oo4,
    CONTINUOUS_MODE,
    HIGH_DEMAND_MODE,
    LOW_DEMAND_MODE,
} from "const"
import {
    SilProjectDevice,
    SilResultProjectDevice,
    QidModel,
    QidProduct,
    QidModelSafetyFunction,
} from "server/models"
import { SilArchitecture, SilMode, SilTarget } from "client/utils"
import {
    SilDeviceType,
    SilDeviceNestedType,
    ModuleList,
    LangType,
    moduleIds,
} from "client/store"


export const parseProp = (value: string) => {
    const valueParsed = parseFloat(value)
    return Number.isNaN(valueParsed) ? null : valueParsed
}


interface ParseProps {
    readonly sff?: string
    readonly dc?: string
    readonly pfd?: string
    readonly pfh?: string

    readonly sd?: string
    readonly su?: string
    readonly dd?: string
    readonly du?: string
}
export const parseProps = ({
    sff,
    dc,
    pfd,
    pfh,

    sd,
    su,
    dd,
    du,
}: ParseProps) => ({
    sff: parseProp(sff),
    dc: parseProp(dc),
    pfd: parseProp(pfd),
    pfh: parseProp(pfh),

    sd: parseProp(sd),
    su: parseProp(su),
    dd: parseProp(dd),
    du: parseProp(du),
})


interface SFFProps {
    sd: string
    su: string
    dd: string
    du: string
}
export const sff = (props: SFFProps) => {
    const { su, sd, du, dd } = parseProps(props)
    if (su !== null || sd !== null || du !== null || dd !== null) {
        const num = su + sd + dd
        const deno = su + sd + dd + du

        if (deno === 0) return false // insufficient data
        return num / deno
    }
    return false // insufficient data
}


interface DcProps {
    dd: string
    du: string
}
export const dc = (props: DcProps) => {
    const { dd, du } = parseProps(props)
    if (dd !== null || du !== null) {
        if (du === 0) return false // insufficient data
        return dd / (dd + du)
    }
    return false // insufficient data
}


export const getHFT = (architecture: SilArchitecture) => {
    switch (architecture) {
        case ARCHITECTURE_1oo1:
            return 0
        case ARCHITECTURE_1oo2:
            return 1
        case ARCHITECTURE_2oo2:
            return 0
        case ARCHITECTURE_1oo2D:
            return 1
        case ARCHITECTURE_2oo3:
            return 1
        case ARCHITECTURE_1oo3:
            return 2
        case ARCHITECTURE_2oo4:
            return 2
        default:
            return null
    }
}


export const parseSilValue = (silValue: string, lang: LangType) => {
    if (typeof silValue === "string") {
        if (silValue.indexOf(lang.sil_1) >= 0) return 1
        if (silValue.indexOf(lang.sil_2) >= 0) return 2
        if (silValue.indexOf(lang.sil_3) >= 0) return 3
        if (silValue.indexOf(lang.sil_4) >= 0) return 4
        return 0
    }
    // in case of JSX Elements like <NotCompliant /> and <InsufficientData />
    return 0
}


export const determineSil = (
    value: number,
    mode: SilMode,
    lang: LangType,
) => {
    if (mode === LOW_DEMAND_MODE) { // PFD value used
        if (value < 0.0001) return lang.sil_4
        if (value < 0.001) return lang.sil_3
        if (value < 0.01) return lang.sil_2
        if (value < 0.1) return lang.sil_1
        return lang.notCompliant
    }
    if (mode === HIGH_DEMAND_MODE || mode === CONTINUOUS_MODE) { // PFH value used
        if (value < 0.00000001) return lang.sil_4
        if (value < 0.0000001) return lang.sil_3
        if (value < 0.000001) return lang.sil_2
        if (value < 0.00001) return lang.sil_1
        return lang.notCompliant
    }
    return lang.notCompliant
}


export const getMaxProbability = (
    mode: SilMode,
    targetSil: SilTarget,
) => {
    if (mode === LOW_DEMAND_MODE) {
        // max PFD
        if (!targetSil) return 1
        if (targetSil === 1) return 0.1
        if (targetSil === 2) return 0.01
        if (targetSil === 3) return 0.001
        if (targetSil === 4) return 0.0001
        return null
    }
    if (mode === HIGH_DEMAND_MODE || mode === CONTINUOUS_MODE) {
        // max PFH
        if (!targetSil) return 1
        if (targetSil === 1) return 0.00001
        if (targetSil === 2) return 0.000001
        if (targetSil === 3) return 0.0000001
        if (targetSil === 4) return 0.00000001
        return null
    }
    return null
}


export const getMaxStr = (targetStl: number) => {
    let minStr = 0
    let maxStr = 0

    if (targetStl) {
        const minDegree = targetStl + 4
        const maxDegree = targetStl + 3
        minStr = Math.round(10 ** -minDegree * 10 ** minDegree) / 10 ** minDegree
        maxStr = Math.round(10 ** -maxDegree * 10 ** maxDegree) / 10 ** maxDegree
    }
    return { minStr, maxStr }
}


export const determineStlLevel = (strValue: number) => {
    if (!strValue) return 0
    if (strValue > getMaxStr(1).maxStr) return 0

    let stlLevel = 0
    let stop = false

    while (!stop) {
        const { minStr, maxStr } = getMaxStr(stlLevel)
        if (strValue >= minStr && strValue < maxStr) {
            stop = true
        } else {
            stlLevel += 1
        }
        if (stlLevel > 1000) stop = true // emergency stop
    }
    return stlLevel
}


export const getSilDeviceTitle = (
    device: SilDeviceNestedType | SilProjectDevice | SilResultProjectDevice,
    moduleList: ModuleList,
    lang: LangType,
) => {
    if (!device) return lang.empty

    const isQidDevice = device.qidProductId !== null

    const combinedTitle: string[] = []
    if (isQidDevice) combinedTitle.push(`${lang.source}: ${moduleList[moduleIds.qid].name}`)

    combinedTitle.push(`${lang.name}: ${device.name}`)
    combinedTitle.push(`${lang.manufacturer}: ${device.manufacturer}`)

    if (device.qidModelId) combinedTitle.push(`${lang.model}: ${device.qidModelName}`)
    if (device.qidSafetyFunctionId) combinedTitle.push(`${lang.safetyFunction}: ${device.qidSafetyFunctionName}`)
    if (device.qidOptionId) combinedTitle.push(`${lang.option}: ${device.qidOptionName}`)
    if (device.qidDatapointId) combinedTitle.push(`${lang.datapoint}: ${device.qidDatapointId}`)
    if (device.qidMethod) combinedTitle.push(`${lang.method}: ${lang[device.qidMethod]}`)

    return combinedTitle.join("\n")
}

export const getSilMode = (device: SilDeviceType | SilResultProjectDevice | QidProduct | QidModel | QidModelSafetyFunction) => {
    let mode: SilMode = null
    const { lowDemandMode, highDemandMode, continuousMode } = device
    if (lowDemandMode) mode = LOW_DEMAND_MODE
    else if (highDemandMode) mode = HIGH_DEMAND_MODE
    else if (continuousMode) mode = CONTINUOUS_MODE
    return mode
}
